import { useCallback, useEffect } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import { FormProvider, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router'
import * as z from 'zod'

import { CreatorHeroLogo } from '@/components/layout/header/logo.tsx'
import { Button } from '@/components/ui/button.tsx'
import { Card, CardContent, CardTitle } from '@/components/ui/card.tsx'
import { FormField } from '@/components/ui/form/form-field.tsx'
import { useToast } from '@/components/ui/toast.tsx'
import { useAccount } from '@/modules/accounts/hooks/useAccounts'
import { AppRoutes } from '@/utils/constants'
import { handleError } from '@/utils/error'
import { classNames } from '@/utils/helpers'
import { useIsDarkTheme } from '@/utils/useTheme'

export type ActivateAccountFormValues = z.infer<
  typeof activateAccountFormSchema
>

const activateAccountFormSchema = z
  .object({
    name: z.string().min(2, { message: 'Name must be at least 2 characters' }),
    password: z
      .string()
      .min(8, { message: 'Password must be at least 8 characters' }),
    confirmPassword: z
      .string()
      .min(1, { message: 'Confirm Password is required' }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    path: ['confirmPassword'],
    message: "Passwords don't match",
  })

export const ActivateAccountPage = () => {
  const { isLoggedIn } = useAccount()

  const isDarkMode = useIsDarkTheme()

  const navigate = useNavigate()

  const searchParams = new URLSearchParams(window.location.search)
  const token = searchParams.get('token')

  const form = useForm<ActivateAccountFormValues>({
    mode: 'onChange',
    resolver: zodResolver(activateAccountFormSchema),
    defaultValues: {
      name: '',
      password: '',
      confirmPassword: '',
    },
  })

  const { handleSubmit } = form
  const { activateAccount } = useAccount()
  const toast = useToast()

  useEffect(
    () => {
      if (isLoggedIn) {
        navigate(AppRoutes.Overview.asUrl())
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isLoggedIn],
  )

  const onSubmit = useCallback(
    (values: ActivateAccountFormValues) => {
      activateAccount
        .mutateAsync({
          token: token!,
          name: values.name,
          password: values.password,
        })
        .then(() => {
          toast.success(
            'Your account has been activated! You can now login with your E-mail and password 🎉',
          )
          navigate(AppRoutes.Auth.Login.asUrl())
        })
        .catch((error) => {
          if (error.response?.status === 409) {
            handleError(
              'There is already an user with this name in the team. Please choose another one.',
            )

            return
          }

          handleError(
            error,
            'Oops, there was an error. Maybe your link was already used?',
          )
        })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  return (
    <div
      className={classNames(
        "grid h-screen w-screen place-items-center bg-[url('/login-background.webp')] bg-cover bg-no-repeat",
        {
          "bg-[url('/login-background-dark.png')]": isDarkMode,
        },
      )}
    >
      <Card className="flex w-full max-w-xl flex-col gap-6 p-8">
        <CardTitle className="flex flex-col items-center justify-center gap-4">
          <CreatorHeroLogo width={215} />
          <div className=" text-sm font-normal text-secondary-300">
            Please enter your name and your password to finish the registration
          </div>
        </CardTitle>
        <CardContent className="p-0">
          <FormProvider {...form}>
            <form
              className="grid gap-8"
              onSubmit={handleSubmit(onSubmit)}
            >
              <div className="grid gap-4">
                <FormField
                  name="name"
                  label="Your Name"
                  placeholder="John Doe"
                  type="text"
                />

                <FormField
                  name="password"
                  label="Password"
                  placeholder="Password (at least 8 character)"
                  type="password"
                  isPassword
                />

                <FormField
                  name="confirmPassword"
                  label="Confirm Password"
                  placeholder="Password (at least 8 character)"
                  type="password"
                  isPassword
                />
              </div>

              <Button
                disabled={!form.formState.isDirty || !form.formState.isValid}
                className="w-full"
              >
                Get started
              </Button>
            </form>
          </FormProvider>
        </CardContent>
      </Card>
    </div>
  )
}
